import React from 'react';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { useTranslation } from 'react-i18next';

import { Enrollment } from './models/Enrollment';
import ConfirmExitDialog from './ConfirmExitDialog';
import TopNavActions from './TopNavActions';
import { Button } from '@mui/material';

interface AboutThisStudyViewProps {
  enrollment: Enrollment;
  onPageChange: (page: string) => void;
}

const AboutThisStudyView: React.FC<AboutThisStudyViewProps> = ({ onPageChange }) => {
  const imagePath = '/assets/images/Microphone.png';
  const [openExitDialog, setOpenExitDialog] = React.useState(false);
  const { t } = useTranslation('enrollment');

  const handleNext = () => {
    onPageChange('expectations-and-requirements');
  };

  const handleBack = () => {
    onPageChange('back');
  };

  return (
    <React.Fragment>
      <TopNavActions
        onBack={handleBack}
        onExit={() => setOpenExitDialog(true)}
      />
      <ConfirmExitDialog
        open={openExitDialog}
        onClose={() => setOpenExitDialog(false)}
        onExit={() => { window.location.href = '/'; }}
      />
      <Typography variant="h4" component='h1' id="page-heading" tabIndex={-1} sx={{ mb: 2 }}>
        {t("About this Study")}
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        {t("Voice as a Biomarker of Health is a study from the Bridge2AI-Voice Consortium with the mission to create a large ethically-sourced and diverse voice database linked to other health information to diagnose diseases.")}
      </Typography>
      <img
        src={imagePath}
        alt="Microphone"
        aria-label="Microphone"
        style={{ width: '100%', maxWidth: 800, display: 'block', margin: '0 auto', marginBottom: 24 }}
      />
      <Typography variant="body1" sx={{ mb: 3 }}>
        {t("The Bridge2AI-Voice consortium is composed of researchers from many different universities in Canada and the US that are using voice and speech combined with artificial intelligence to help patients get diagnosed earlier and treated better.")}
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        {t("Voice as a Biomarker of Health is a large multi-institutional study funded by the National Institutes of Health's (NIH) Bridge2AI program.")}
      </Typography>
      <Typography variant="body1">
        {t("We collect voice and health data from many participants with many different conditions and disorders from 5 broad categories:")}
      </Typography>

      <List>
        <ListItem>
          <ListItemText
            primary={
              <Typography variant="body1" fontWeight={600}>
                1. {t("Voice Disorders")}
              </Typography>
            }
            secondary={
              <Typography variant="body1">
                {t("Glottic Insufficiency / Presbyphonia, Laryngeal Cancer, Laryngitis, Lesions of the vocal cord (nodule, polyp, cyst), Muscle Tension Dysphonia (MTD), Recurrent Laryngeal Papilloma (RRP), Spasmodic Dysphonia / Laryngeal Tremor, Unilateral Vocal Fold Paralysis")}
              </Typography>
            }
          />
        </ListItem>
        <ListItem >
          <ListItemText
            primary={
              <Typography variant="body1" fontWeight={600}>
                2. {t("Respiratory Disorders")}
              </Typography>}
            secondary={
              <Typography variant="body1">
                {t("Asthma, Airway Stenosis, Chronic Cough, COPD")}
              </Typography>
            }
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary={
              <Typography variant="body1" fontWeight={600}>
                3. {t("Neurological and Neurodegenerative Disorders")}
              </Typography>
            }
            secondary={
              <Typography variant="body1">
                {t("Alzheimer’s, Dementia, or Mild Cognitive Impairment, Amyotrophic Lateral Sclerosis (ALS), Huntington’s disease, Parkinson’s disease")}
              </Typography>
            }
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary={
              <Typography variant="body1" fontWeight={600}>
                4. {t("Mood and Psychiatric Disorders")}
              </Typography>
            }
            secondary={
              <Typography variant="body1">
                {t("Depression, Bipolar disorder, Anxiety disorder")}
              </Typography>
            }
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary={
             <Typography variant="body1" fontWeight={600}>
               5. {t("Pediatric Disorders")}
             </Typography>
            }
            secondary={
              <Typography variant="body1">
                {t("Autism Spectrum Disorder (ASD), Speech Delay")}
              </Typography>
            }
          />
        </ListItem>
      </List>

      <Typography variant="body1">
        {t("We are also interested in collecting control data. This means that your voice can be compared to the voices of those with the conditions listed above, so we can better understand how to identify and distinguish them.")}
      </Typography>

      <Button variant="contained" onClick={handleNext} style={{ marginTop: 24, marginBottom: 24 }}>
        {t("Next")}
      </Button>
    </React.Fragment>
  );
}

export default AboutThisStudyView;
